import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { NoData } from "@/components/common/section-components/NoData";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { PurchaseOrderIntentActions } from "@/components/common/section-components/action/PurchaseOrderIntentActions";
import { PurchaseOrderIntentFilter } from "@/components/common/section-components/filters/PurchaseOrderIntentFilter";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";

import { PurchaseOrderIntentStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";

import {
  Flex,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";

import { useNavigate } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";

import {
  clearFiltersHandler,
  fetchPurchaseOrderIntentListHandler,
  getDefaultPurchaseOrderIntentFilters,
  submitFiltersHandler,
} from "./schema/handler-functions";
import {
  PurchaseOrderIntentListItem,
  PurchaseOrderIntentListResponse,
  purchaseOrderIntentDefaultValues,
} from "./schema/purchase-order-intent.schema";
import { purchaseOrderIntentHeaderElements } from "./schema/header-elements";

export const PurchaseOrderIntentPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  useEffect(() => {
    setIsActive(true);
  }, []);

  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const { control, handleSubmit, setValue } = useForm<FieldValues>({
    defaultValues: purchaseOrderIntentDefaultValues as any,
  });

  const values = useWatch({ control });
  const filtersEmpty = useMemo(
    () => Object.values(values ?? {}).every((v) => isEmpty(v)),
    [values],
  );

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<any>();
  const [page, setPage] = useState<number>(1);

  const [list, setList] = useState<PurchaseOrderIntentListResponse | null>(null);

  const fetchList = async (filters?: any, showLoad = true) => {
    await fetchPurchaseOrderIntentListHandler({
      filters,
      showLoad,
      setLoading,
      setList,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {
      const initialFilters = getDefaultPurchaseOrderIntentFilters();
      setCurrentFilters(initialFilters);
      setFiltersApplied(true);
      fetchList(initialFilters);
    }
  }, [isActive]);

  const submitFilters = async (event: FieldValues) => {
    await submitFiltersHandler({
      event,
      setPage,
      fetchList,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      filtersEmpty,
    });
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      setValue,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      setCurrentFilters,
      fetchList,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (item: PurchaseOrderIntentListItem) => {
    navigate(AppRoutes.PURCHASE_ORDER_INTENT_DETAIL.replace(":id", String(item.id)));
  };

  const getActionElements = (item: PurchaseOrderIntentListItem) => (
    <PurchaseOrderIntentActions handleDetail={handleDetail} item={item} />
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader title="Intención de compra" loading={loading} />

      <PurchaseOrderIntentFilter
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        filtersApplied={filtersApplied}
        disableFilters={loading}
        loadingFilters={loading}
        disableApply={filtersEmpty && !filtersApplied}
      />

      <Flex flexDir="column">
        {Boolean(!loading && !list?.listado?.length) && <NoData />}

        {Boolean(!loading && list?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection

              items={
                list?.listado?.map((el: PurchaseOrderIntentListItem) => ({
                  ...el,
                  displayFechaSolicitud: el?.fechaSolicitud
                    ? dayjs(el?.fechaSolicitud).format("DD/MM/YYYY")
                    : "-",
                  displayTitularCuenta:
                    el?.titularCuenta && el?.cuenta
                      ? `${el.titularCuenta} - ${el.cuenta}`
                      : el?.titularCuenta ?? "-",
                  displayEstado: (
                    <RequestStatusBadge
                      status={
                        el?.estadoDescripcion as unknown as PurchaseOrderIntentStatus
                      }
                    />
                  ),
                })) ?? []
              }
              headers={purchaseOrderIntentHeaderElements}
              actionElements={getActionElements}
            />

            {Number(list?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={list?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};